import { FormattedMessage } from 'react-intl';
import Section from './Section';
import Button from '@/components/Button/Button';
import { styled } from '@mui/material/styles';
import FilterChip from './FilterChip';
import {
  FilterDetails,
  ResourcesSidebarFilters,
  ResourcesSidebarSections,
  getLabelForFilterValue,
} from './ResourcesSidebarHelpers';
import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters, fetchConfig } from '@/lib/api';
import CircularProgress from '@mui/material/CircularProgress';
import { ga } from '@/lib/analytics';

type AppliedFilterItem = {
  id: ResourcesSidebarSections;
  key: string;
  label: ReactNode;
  value: unknown;
};

const AppliedFilters = ({
  filters,
  onRemoveFilters,
}: {
  filters: Record<ResourcesSidebarSections, FilterDetails>;
  onRemoveFilters: () => void;
}) => {
  const { data: filtersData, isPending: isFiltersPending } = useQuery(
    {
      queryKey: ['filters'],
      queryFn: fetchFilters,
    },
  );
  const { data: configData, isPending: isConfigPending } = useQuery({
    queryKey: ['config'],
    queryFn: fetchConfig,
  });

  const isLoading = isFiltersPending || isConfigPending;

  const filterItems: AppliedFilterItem[] = [];

  Object.entries(filters).forEach(([key, filter]) => {
    const filterKey = key as ResourcesSidebarSections;

    // Skip navigation as it's not a filter type
    if (filterKey === 'navigation') {
      return;
    }

    if (!Array.isArray(filter.value) || filter.value.length === 0) {
      return;
    }

    if (filterKey === 'releaseDate') {
      const from = filter.value[0];
      const to = filter.value[1];

      const fromLabel =
        from !== undefined
          ? getLabelForFilterValue(
              'releaseDate',
              from as string,
              filtersData,
              configData,
            )
          : null;

      const toLabel =
        to !== undefined
          ? getLabelForFilterValue(
              'releaseDate',
              to as string,
              filtersData,
              configData,
            )
          : null;

      const rangeLabel =
        fromLabel && toLabel ? (
          <>
            {fromLabel}-{toLabel}
          </>
        ) : (
          fromLabel || toLabel
        );

      filterItems.push({
        id: filterKey,
        key: `${filterKey}+range`,
        label: (
          <>
            {filter.label}={rangeLabel}
          </>
        ) as ReactNode,
        value: filter.value,
      });

      return;
    }

    const filterId = filterKey as ResourcesSidebarFilters;

    filter.value.forEach((value) => {
      const label = getLabelForFilterValue(
        filterId,
        value as string | number | string[] | [number, number],
        filtersData,
        configData,
      );

      filterItems.push({
        id: filterKey,
        key: `${filterKey}+${JSON.stringify(value)}`,
        label: (
          <>
            {filter.label}={label}
          </>
        ) as ReactNode,
        value,
      });
    });
  });

  const handleRemove = (key: string) => {
    const filterItem = filterItems.find((item) => item.key === key);
    const id = filterItem?.id;

    if (!id || !filterItem) return;

    const currentFilter = filters[id];
    const filterType = ga.filterType(id);

    ga.trackMouseClick({
      component: 'filter_remove',
      filter_type: filterType,
      filter_value: ga.formatFilterValue(filterType, filterItem.value),
    });

    if (id === 'releaseDate') {
      currentFilter.setter(currentFilter.defaultValue);
      return;
    }

    const filteredValue = currentFilter.value.filter(
      (value) =>
        JSON.stringify(value) !== JSON.stringify(filterItem.value),
    );
    const newValue =
      filteredValue.length === 0
        ? currentFilter.defaultValue
        : filteredValue;

    currentFilter.setter(newValue);
  };

  return (
    <Section
      title={
        <FormattedMessage
          id="appliedFilters"
          defaultMessage="Applied Filters"
        />
      }
      titleAction={
        <StyledButton
          onClick={() => {
            ga.trackMouseClick({
              component: 'filter_clear_all',
            });
            onRemoveFilters();
          }}
          aria-label="Clear all filters"
        >
          <FormattedMessage
            id="clearAll"
            defaultMessage="Clear All"
          />
        </StyledButton>
      }
      aria-label="Applied filters"
      defaultOpen
    >
      {isLoading ? (
        <LoaderWrapper role="status" aria-label="Loading filters">
          <CircularProgress size={24} />
        </LoaderWrapper>
      ) : filterItems.length > 0 ? (
        <Chips>
          {filterItems.map(({ id, key, label }) => (
            <FilterChip
              key={key}
              label={label}
              onRemove={() => handleRemove(key)}
              aria-label={`Remove ${
                typeof label === 'string' ? label : id
              } filter`}
            />
          ))}
        </Chips>
      ) : (
        <NoFiltersLabel>
          <FormattedMessage
            id="noFilters"
            defaultMessage="No filters applied"
          />
        </NoFiltersLabel>
      )}
    </Section>
  );
};

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '10px',
  lineHeight: '12px',
  color: theme.palette.white,
}));

const Chips = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const NoFiltersLabel = styled('div')(({ theme }) => ({
  margin: `${theme.spacing(2)} auto ${theme.spacing(0)}`,
  padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
  borderRadius: theme.spacing(0.5),
  fontSize: '10px',
  lineHeight: '16px',
  fontWeight: 600,
  height: 24,
}));

const LoaderWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  minHeight: 60,
}));

export default AppliedFilters;
